import { useMemo } from 'react';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { useToast } from '@chakra-ui/react';

import { apiLinkBinders, apiUnlinkBinder } from 'api';
import { APISubmissionPolicyData } from 'types/api.types';
import {
  RUNNER_MC_AUTO,
  RUNNER_MC_HOME,
  WIP_STATUS_SIGNING_AWAITING_BROKER,
} from 'variables/constants';

interface UsePolicyGroupOptions {
  policies: APISubmissionPolicyData[] | undefined;
  refetchSubmission: () => Promise<any>;
}

export const usePolicyGroup = ({
  policies,
  refetchSubmission,
}: UsePolicyGroupOptions) => {
  const toast = useToast();
  const queryClient = useQueryClient();

  const existingGroupId = useMemo(() => {
    if (!policies) return null;
    const linkedPolicy = policies.find((p) => p.policy_group_id);
    return linkedPolicy?.policy_group_id || null;
  }, [policies]);

  const hasGroupedPolicies = useMemo(() => {
    return policies?.some((p) => p.policy_group_id) || false;
  }, [policies]);

  const sortedPolicies = useMemo(() => {
    if (!policies) return [];
    return [...policies].sort((a, b) => {
      const aLinked = !!a.policy_group_id;
      const bLinked = !!b.policy_group_id;
      if (aLinked && !bLinked) return -1;
      if (!aLinked && bLinked) return 1;
      return 0;
    });
  }, [policies]);

  const isPolicyLinked = (policyUuid: string): boolean => {
    const policy = policies?.find((p) => p.uuid === policyUuid);
    return !!policy?.policy_group_id;
  };

  const canLinkBinders = (): boolean => {
    if (!policies) return false;

    if (hasGroupedPolicies) {
      const groupInSigning = policies.some(
        (p) =>
          p.policy_group_id &&
          p.wip_status &&
          p.wip_status >= WIP_STATUS_SIGNING_AWAITING_BROKER,
      );
      if (groupInSigning) {
        return false;
      }
    }

    // Filter for linkable policies: Auto/Home, not already linked, not in signing process
    const linkablePolicies = policies.filter(
      (p) =>
        (p.runner === RUNNER_MC_AUTO || p.runner === RUNNER_MC_HOME) &&
        !p.policy_group_id &&
        (!p.wip_status || p.wip_status < WIP_STATUS_SIGNING_AWAITING_BROKER),
    );
    if (hasGroupedPolicies) {
      return linkablePolicies.length >= 1;
    }
    return linkablePolicies.length >= 2;
  };

  const linkBindersMutation = useMutation({
    mutationFn: ({
      policyUuids,
      policyGroupId,
    }: {
      policyUuids: string[];
      policyGroupId?: string;
    }) => apiLinkBinders(policyUuids, policyGroupId),
    onSuccess: async (_data, variables) => {
      toast({
        title: 'Binders linked successfully',
        status: 'success',
        duration: 3000,
        isClosable: true,
        position: 'top',
      });
      // Invalidate groupedBillingSync for all linked policies
      for (const policyUuid of variables.policyUuids) {
        await queryClient.invalidateQueries({
          queryKey: ['groupedBillingSync', policyUuid],
        });
      }
      // Also invalidate for any existing grouped policies
      if (policies) {
        for (const policy of policies) {
          if (policy.policy_group_id) {
            await queryClient.invalidateQueries({
              queryKey: ['groupedBillingSync', policy.uuid],
            });
          }
        }
      }
      // Invalidate policyGroupStatus queries
      await queryClient.invalidateQueries({
        queryKey: ['policyGroupStatus'],
      });
      // Invalidate groupedBinderStatus queries (binder state was reset on backend)
      await queryClient.invalidateQueries({
        queryKey: ['groupedBinderStatus'],
      });
      // Invalidate form queries so policy_group_id is available for usePolicyGroupId
      for (const policyUuid of variables.policyUuids) {
        await queryClient.invalidateQueries({
          queryKey: ['app_form', policyUuid],
        });
        await queryClient.invalidateQueries({
          queryKey: ['home_form', policyUuid],
        });
      }
      await refetchSubmission();
    },
    onError: (error: any) => {
      toast({
        title: 'Failed to link binders',
        description: error?.error,
        status: 'error',
        duration: 5000,
        isClosable: true,
        position: 'top',
      });
    },
  });

  const unlinkBinderMutation = useMutation({
    mutationFn: (policyUuid: string) => apiUnlinkBinder(policyUuid),
    onSuccess: async (data: any, policyUuid) => {
      toast({
        title: 'Binder unlinked',
        description: data?.warning,
        status: 'warning',
        duration: 5000,
        isClosable: true,
        position: 'top',
      });
      // Invalidate groupedBillingSync for unlinked policy
      await queryClient.invalidateQueries({
        queryKey: ['groupedBillingSync', policyUuid],
      });
      // Also invalidate for any remaining grouped policies
      if (policies) {
        for (const policy of policies) {
          if (policy.policy_group_id) {
            await queryClient.invalidateQueries({
              queryKey: ['groupedBillingSync', policy.uuid],
            });
          }
        }
      }
      // Invalidate policyGroupStatus queries
      await queryClient.invalidateQueries({
        queryKey: ['policyGroupStatus'],
      });
      // Invalidate groupedBinderStatus queries (binder state was reset on backend)
      await queryClient.invalidateQueries({
        queryKey: ['groupedBinderStatus'],
      });
      // Invalidate form queries so policy_group_id is cleared for usePolicyGroupId
      await queryClient.invalidateQueries({
        queryKey: ['app_form', policyUuid],
      });
      await queryClient.invalidateQueries({
        queryKey: ['home_form', policyUuid],
      });
      await refetchSubmission();
    },
    onError: (error: any) => {
      toast({
        title: 'Failed to unlink binder',
        description: error?.error,
        status: 'error',
        duration: 5000,
        isClosable: true,
        position: 'top',
      });
    },
  });

  const linkBinders = async (policyUuids: string[], policyGroupId?: string) => {
    await linkBindersMutation.mutateAsync({ policyUuids, policyGroupId });
  };

  const unlinkBinder = async (policyUuid: string) => {
    await unlinkBinderMutation.mutateAsync(policyUuid);
  };

  return {
    existingGroupId,
    hasGroupedPolicies,
    sortedPolicies,
    isPolicyLinked,
    canLinkBinders,
    linkBinders,
    unlinkBinder,
    isLinking: linkBindersMutation.isPending,
    isUnlinking: unlinkBinderMutation.isPending,
  };
};
